import { combineReducers } from 'redux'

import { ApplicationState } from '.'
import { Session, SessionStatus } from '../../api'
import { api } from '../../client'
import { bind, unique } from '../../utils'
import createAction from '../redux-utils/create-action'
import { Reducer } from '../types'
import { createRequest } from './communication.duck'

const SESSIONS = 'sessions'

export const SESSIONS_GET_SUCCESS = `${SESSIONS} GET_SUCCESS`
export const SESSIONS_DELETE_SUCCESS = `${SESSIONS} DELETE_SUCCESS`
export const SESSIONS_UPDATED = `${SESSIONS} UPDATED`
export const SESSIONS_SCHEDULED = `${SESSIONS} SCHEDULED`
export const SESSIONS_COMPLETED = `${SESSIONS} COMPLETED`

interface ExtendedSession extends Session {
  processId: number
}

interface ByIdState {
  [id: number]: ExtendedSession
}

const byId: Reducer<ByIdState> = (state = {}, action) => {
  const { type, payload } = action

  switch (type) {
    case SESSIONS_GET_SUCCESS: {
      const { processId } = payload
      const sessions = payload.sessions as Session[]

      return sessions.reduce(
        (reduction, session) => ({
          ...reduction,
          [session.id]: {
            ...session,
            processId,
          },
        }),
        { ...state }
      )
    }
    case SESSIONS_DELETE_SUCCESS: {
      const { processId } = payload
      const sessions = payload.sessions as Session[]

      return sessions.reduce(
        (reduction, session) => ({
          ...reduction,
          [session.id]: {
            ...session,
            processId,
          },
        }),
        { ...state }
      )
    }

    case SESSIONS_COMPLETED: {
      const { id, ...rest } = payload

      return {
        ...state,
        [id]: {
          ...state[id],
          status: SessionStatus.Done,
          ...rest,
        },
      }
    }

    case SESSIONS_UPDATED: {
      const { sessionId, ...rest } = payload

      return {
        ...state,
        [sessionId]: {
          ...state[sessionId],
          ...rest,
        },
      }
    }

    case SESSIONS_SCHEDULED: {
      const { id, schedule } = payload

      return {
        ...state,
        [id]: {
          ...state[id],
          date: schedule.date,
          status: SessionStatus.Scheduled,
        },
      }
    }

    default:
      return state
  }
}

type IdsState = number[]
const ids: Reducer<IdsState> = (state = [], action) => {
  const { type, payload } = action

  switch (type) {
    case SESSIONS_GET_SUCCESS:
      return unique([...state, ...payload.sessions.map(({ id }) => id)])
    case SESSIONS_DELETE_SUCCESS:
      return unique([...state, ...payload.sessions.map(({ id }) => id)])

    default:
      return state
  }
}

interface ByProcessIdState {
  [key: number]: number[]
}

const byProcessId: Reducer<ByProcessIdState> = (state = [], action) => {
  const { type, payload } = action
  switch (type) {
    case SESSIONS_GET_SUCCESS:
      return {
        ...state,
        [payload.processId]: payload.sessions.map((session) => session.id),
      }
    case SESSIONS_DELETE_SUCCESS:
      return {
        ...state,
        [payload.processId]: payload.sessions.map((session) => session.id),
      }

    default:
      return state
  }
}

export interface SessionsState {
  byProcessId: ByProcessIdState
  byId: ByIdState
  ids: IdsState
}

export default combineReducers({
  byProcessId,
  ids,
  byId,
})

/* REQUESTS */

const sessionsGetSuccess = ({ results }, processId) =>
  createAction(SESSIONS_GET_SUCCESS, {
    processId,
    sessions: results,
  })

const handleSessionScheduled = (payload, id, schedule) =>
  createAction(
    SESSIONS_SCHEDULED,
    { id, schedule },
    {
      toast: {
        message: 'sessionScheduled',
      },
    }
  )

const handleSessionCompleted = (payload, id, values) =>
  createAction(
    SESSIONS_COMPLETED,
    { id, ...values },
    {
      toast: {
        message: 'sessionCompleted',
      },
    }
  )

const handleSessionUpdated = (res, sessionId, params) =>
  createAction(
    SESSIONS_UPDATED,
    { sessionId, ...params },
    {
      toast: {
        message: 'sessionUpdated',
      },
    }
  )

// const handleSessionDelete = (response, programId) =>
//   createAction(PROCESSES_BY_PROGRAM_ID_GET_SUCCESS, {
//     programId,
//     processes: response.results,
//   })
const handleSessionDelete = ({ results }, sessionId, processId) =>
  createAction(SESSIONS_DELETE_SUCCESS, {
    processId,
    sessions: results,
  })

export const sessionsGetRequest = createRequest({
  namespace: `${SESSIONS}-get`,
  request: bind(api)(api.getSessions),
  successHandler: sessionsGetSuccess,
})

export const sessionScheduleRequest = createRequest({
  namespace: `${SESSIONS}-schedule`,
  request: bind(api)(api.scheduleSession),
  successHandler: handleSessionScheduled,
})

export const sessionCompleteRequest = createRequest({
  namespace: `${SESSIONS}-complete`,
  request: bind(api)(api.completeSession),
  successHandler: handleSessionCompleted,
})

export const sessionUpdateRequest = createRequest({
  namespace: `${SESSIONS}-update`,
  request: bind(api)(api.updateSession),
  successHandler: handleSessionUpdated,
})

export const sessionDeleteRequest = createRequest({
  namespace: `${SESSIONS}-delete`,
  request: bind(api)(api.deleteSession),
  successHandler: handleSessionDelete,
})

/* SELECTORS */
export const selectAllSessions = ({ sessions }: ApplicationState) =>
  sessions.ids.map((id) => sessions.byId[id])

export const selectSessionsByProcessId = (processId) => ({
  sessions: { byProcessId, byId },
}: ApplicationState) => {
  const sessionIds = byProcessId[processId]

  if (!sessionIds) return []

  let doneSessions: any[] = []
  let tbaSessions: any[] = []

  let scheduledSession

  sessionIds
    .map((sessionId) => byId[sessionId])
    .forEach((session) => {
      switch (session.status) {
        case SessionStatus.Done:
          doneSessions = [...doneSessions, session]
          break
        case SessionStatus.Scheduled:
          scheduledSession = session
          break
        case SessionStatus.Pending:
          tbaSessions = [...tbaSessions, session]
          break
      }
    })

  let sessions = doneSessions.sort((a, b) => a.date - b.date)

  if (scheduledSession) {
    sessions = [...sessions, scheduledSession]
  }

  return [...sessions, ...tbaSessions]
}



// WEBPACK FOOTER //
// ./src/redux/ducks/sessions.duck.ts